// src/pages/DebtStudio/components/DealForm/DealInfo.jsx
import React from "react";
import {
  useDebtStudio,
  applyFinancingTypeTemplate,
} from "pages/DebtStudio/context/DebtStudioContext";
import {
  SHARING_OPTIONS,
  normalizeSharing,
} from "lib/permissions";
import SectionCard from "components/SectionCard";
import { TextInput, DropdownSelect, NumberInput } from "components/InputStyled";
import IssuerSelect from "./IssuerSelect";

export default function DealInfo({ isReadOnly: isReadOnlyProp } = {}) {
  const {
    form,
    setForm,
    isReadOnly: ctxReadOnly,
    canChangeSharing,
  } = useDebtStudio();

  // Allow either prop or context (prop wins if provided)
  const isReadOnly = isReadOnlyProp ?? ctxReadOnly;

  const setStr = (k, v) => {
    if (isReadOnly) return;
    setForm({ ...form, [k]: v });
  };

  const setNum = (k, v) => {
    if (isReadOnly) return;
    setForm({ ...form, [k]: v });
  };

  return (
    <SectionCard title="Issue Information">
      <div className="space-y-4">
        {/* Financing type */}
        <div className="w-full md:w-32">
          <DropdownSelect
            label="Financing Type"
            value={form.financingType || "bond"}
            onChange={(v) => {
              if (isReadOnly) return;
              setForm((prev) => applyFinancingTypeTemplate(prev, v));
            }}
            options={[
              { value: "bond", label: "Bond" },
              { value: "loan", label: "Loan" },
            ]}
            className="w-full"
            disabled={isReadOnly}
          />
        </div>

        {/* Main fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Issuer */}
          <IssuerSelect isReadOnly={isReadOnly} />

          {/* Issue name */}
          <TextInput
            label="Issue Name"
            value={form.dealName || ""}
            onChange={(v) => setStr("dealName", v)}
            placeholder="Enter issue name"
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Par amount */}
          <NumberInput
            label={
              String(form?.financingType || "").toLowerCase() === "loan"
                ? "Principal Amount"
                : "Par Amount"
            }
            type="currency"
            value={form.parAmount ?? ""}
            onChange={(n) => setNum("parAmount", n)}
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Lien priority */}
          <DropdownSelect
            label="Lien Priority"
            value={form.lienPriority || ""}
            onChange={(v) => setStr("lienPriority", v)}
            options={[
              { value: "senior", label: "Senior" },
              { value: "subordinate", label: "Subordinate" },
            ]}
            placeholder="Select lien priority"
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Deal type */}
          <DropdownSelect
            label="Purpose"
            value={form.dealType || ""}
            onChange={(v) => setStr("dealType", v)}
            options={[
              { value: "new_money", label: "New Money" },
              { value: "refunding", label: "Refunding" },
              { value: "combined", label: "Combination" },
            ]}
            placeholder="Select type"
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Interest mode */}
          <DropdownSelect
            label="Interest Mode"
            value={form.interestMode || "fixed"}
            onChange={(v) => setStr("interestMode", v)}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "floating", label: "Floating" },
            ]}
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Tax status */}
          <DropdownSelect
            label="Tax Status"
            value={form.taxStatus || "tax_exempt"}
            onChange={(v) => setStr("taxStatus", v)}
            options={[
              { value: "tax_exempt", label: "Tax-exempt" },
              { value: "taxable", label: "Taxable" },
              { value: "amt", label: "AMT" },
            ]}
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Status */}
          <DropdownSelect
            label="Status"
            value={form.status || "draft"}
            onChange={(v) => setStr("status", v)}
            options={[
              { value: "draft", label: "Draft" },
              { value: "prelim", label: "Preliminary" },
              { value: "final", label: "Final" },
            ]}
            className="w-full"
            disabled={isReadOnly}
          />

          {/* Sharing */}
          <DropdownSelect
            label="Sharing"
            value={normalizeSharing(form.sharing)}
            onChange={(v) => {
              if (!canChangeSharing) return;
              setStr("sharing", v);
            }}
            options={SHARING_OPTIONS}
            className="w-full"
            disabled={!canChangeSharing}
          />
        </div>
      </div>
    </SectionCard>
  );
}
